import { differenceInDays } from "date-fns";
import { format, formatInTimeZone } from "date-fns-tz";

export const displayDateTimeInPracticeTimeZone = (
  dateTime: string,
  practiceTimeZone: string,
) => {
  const dateTimeInPracticeTimeZone = new Date(dateTime);
  return formatInTimeZone(
    dateTimeInPracticeTimeZone,
    practiceTimeZone,
    "MM/dd/yyyy hh:mm a",
  );
};

export const displayDateInPracticeTimeZone = (
  date: string,
  practiceTimeZone: string,
  outputFormat = "MM/dd/yyyy",
) => {
  const dateInPracticeTimeZone = new Date(date);
  return formatInTimeZone(
    dateInPracticeTimeZone,
    practiceTimeZone,
    outputFormat,
  );
};

export const displayTimeInPracticeTimeZone = (
  time: string,
  practiceTimeZone: string,
  outputFormat = "hh:mm a",
) => {
  const timeInPracticeTimeZone = new Date(time);
  return formatInTimeZone(
    timeInPracticeTimeZone,
    practiceTimeZone,
    outputFormat,
  );
};

export const displayDateTimeInUserTimeZone = (
  dateTime: string,
  outputFormat = "MM/dd/yyyy hh:mm a",
) => {
  const dateTimeInUserTimeZone = new Date(dateTime);
  return format(dateTimeInUserTimeZone, outputFormat);
};

export const displayDateInUserTimeZone = (
  date: string,
  outputFormat = "MM/dd/yyyy",
) => {
  const dateInUserTimeZone = new Date(date);
  return format(dateInUserTimeZone, outputFormat);
};

export const displayTimeInUserTimeZone = (
  time: string,
  outputFormat = "hh:mm a",
) => {
  const timeInUserTimeZone = new Date(time);
  return format(timeInUserTimeZone, outputFormat);
};

// Only show both separated by a / if they are different
export const displayInBothPracticeAndUserTimeZone = (
  dateTime: string,
  practiceTimeZone: string,
) => {
  const dateTimeInPracticeTimeZone = displayDateTimeInPracticeTimeZone(
    dateTime,
    practiceTimeZone,
  );
  const dateTimeInUserTimeZone = displayDateTimeInUserTimeZone(dateTime);
  if (dateTimeInPracticeTimeZone !== dateTimeInUserTimeZone) {
    return `${dateTimeInPracticeTimeZone} / ${dateTimeInUserTimeZone}`;
  }
  return dateTimeInPracticeTimeZone;
};

export const inLastNDays = (
  date: string,
  n: number,
  practiceTimeZone: string,
) => {
  const dateInPracticeTimeZone = new Date(date);
  return differenceInDays(dateInPracticeTimeZone, new Date()) <= n;
};

/**
 * Converts an IANA timezone name (e.g., "America/New_York") to its abbreviation (e.g., "EST")
 * @param timezone - The IANA timezone name
 * @param referenceDate - The instant to resolve the abbreviation AT. DST-varying
 *   zones (EST vs EDT) resolve differently depending on the date, so pass the
 *   moment being displayed — otherwise the abbreviation reflects "now" and can
 *   contradict a time formatted from a different DST period (e.g. a January
 *   appointment shown as "EST" while rendered in July would otherwise read
 *   "EDT"). Defaults to now for callers labeling the current moment.
 * @returns The timezone abbreviation
 */
export function getRawTimezoneAbbreviation(
  timezone: string,
  referenceDate: Date = new Date(),
): string {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "short",
  });

  const parts = formatter.formatToParts(referenceDate);
  const timeZonePart = parts.find((part) => part.type === "timeZoneName");

  return timeZonePart?.value ?? timezone;
}

/**
 * Returns the abbreviation for the user's local timezone (e.g., "PT" for America/Los_Angeles).
 * @param referenceDate - see getRawTimezoneAbbreviation; pass the displayed instant.
 */
export function getUserTimezoneAbbreviation(
  referenceDate: Date = new Date(),
): string {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return getRawTimezoneAbbreviation(userTimeZone, referenceDate);
}

/**
 * Converts an IANA timezone name (e.g., "America/New_York") to its abbreviation (e.g., "EST"),
 * and returns an empty string if it's the same timezone as the user's timezone
 * @param timezone - The IANA timezone name
 * @param referenceDate - see getRawTimezoneAbbreviation; pass the displayed instant.
 * @returns The timezone abbreviation or empty string if same as user's timezone
 */
export function getTimezoneAbbreviation(
  timezone: string,
  referenceDate: Date = new Date(),
): string {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!timezone) {
    console.error("Timezone is undefined");
    return "";
  }
  if (timezone === userTimeZone) {
    return "";
  }
  return getRawTimezoneAbbreviation(timezone, referenceDate);
}
